import {LeaderboardRankings} from '@/api/Leaderboards/leaderboard.types';
import {NewPageHeading} from '@/components/MLS/NewPageHeading/NewPageHeading';
import {isTvMode} from '@/utils/utils';
import {useCallback, useMemo, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {useProfiles} from '../../../api/User/useProfiles';
import ChevronLeft from '../../../assets/img/icons/mls-icons/chevron-left.svg?react';
import {ScrollableList} from '../../../components/ScrollableList/ScrollableList';
import {useAuthStatus} from '../../../firebase/useAuthStatus.hook';
import {useLeaderboardSearch} from '../../../hooks/useLeaderboardSearch';
import {LeaderboardPageContainer} from '../LeaderboardPageContainer/LeaderboardPageContainer';
import styles from './LeaderboardDetails.module.scss';
import {LeaderboardInfoPane} from './LeaderboardInfoPane/LeaderboardInfoPane';
import {LeaderboardSearchBar} from './LeaderboardSearchBar/LeaderboardSearchBar';
import {ListCard} from './ListCard/ListCard';
import {ListHeading} from './ListHeading/ListHeading';

export const LeaderboardDetails = ({leaderboard}: {leaderboard: LeaderboardRankings}) => {
  const navigate = useNavigate();
  const {loggedIn, checkingStatus} = useAuthStatus();
  const publicView = !loggedIn;
  const showBackButton = !isTvMode() && loggedIn;
  const {currentProfile} = useProfiles();

  // TODO ensure backward-compatability is sorted out, remove hardcode
  // const showMLSLogo =
  //   leaderboard.leaderboard.locations?.[0].mboSiteId === '-2147480770' || !!leaderboard.leaderboard.showMLSBranding;

  const showMLSLogo = true; // TEMP while requirements are being worked out for logo configuration.

  const [searchIndex, setSearchIndex] = useState<number>(0); // used to cycle through search results
  const userRank = useRef<number>(); // calculated when creating Cards

  const {updateQuery, indexResults} = useLeaderboardSearch({rankings: leaderboard.rankings});

  // creates ListCard component array
  const createCards = useCallback(
    ({rankings, searchResults}: {rankings?: LeaderboardRankings['rankings']; searchResults?: number[]}) =>
      rankings?.map((ranking, index) => {
        const player =
          Boolean(ranking.profileId && ranking.profileId === currentProfile?._id) ||
          Boolean(ranking.clientId && ranking.clientId === currentProfile?.mboDetails?.clientId);
        if (player) {
          userRank.current = index;
        }
        const search = !!searchResults?.includes(index);
        return <ListCard key={ranking._id} highlight={{player, search}} player={ranking} />;
      }),
    [currentProfile?._id, currentProfile?.mboDetails?.clientId]
  );

  // creates/recreate cards based on rankings and search results
  const items = useMemo(() => {
    setSearchIndex(0);
    return createCards({
      rankings: leaderboard.rankings,
      searchResults: indexResults,
    });
  }, [createCards, leaderboard, indexResults]);

  // set fixed parameters for rankings list
  // const itemHeight = useMemo(() => (window.innerWidth < 768 ? 52 : window.innerWidth < 1800 ? 64 : 88), []);
  const itemHeight = useMemo(() => (window.innerWidth < 768 ? 56 : 60), []);
  const startingItem = useMemo(() => (userRank.current ? userRank.current - 3 : 0), []);

  const scrollToItem = useMemo(() => {
    return indexResults.length ? Math.max(indexResults[searchIndex] - 2, 0) : undefined;
  }, [indexResults, searchIndex]);
  const onSearchChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => updateQuery(e.currentTarget.value),
    [updateQuery]
  );

  return (
    <LeaderboardPageContainer>
      <>
        {!checkingStatus && (
          <>
            {publicView && (
              <div className={styles.fkoBanner}>
                <Link to="/fko">
                  <span>
                    Join MyTOCA and get a <b>FREE</b> baseline session!
                  </span>
                </Link>
              </div>
            )}
            <div className="flex min-h-[80px] justify-center xs:justify-between">
              {showMLSLogo ? (
                <>
                  <div className="hidden xs:flex">
                    <NewPageHeading
                      text="Leaderboards"
                      backButton={showBackButton}
                      onClickOverride={() =>
                        navigate(publicView ? '/' : '/leaderboards', {
                          state: {from: `/leaderboards/${leaderboard?.leaderboard?._id}`},
                        })
                      }
                    />
                  </div>
                  <div className="flex items-center w-full min-h-[64px] relative">
                    <button
                      onClick={() =>
                        navigate(publicView ? '/' : '/leaderboards', {
                          state: {from: `/leaderboards/${leaderboard?.leaderboard?._id}`},
                        })
                      }
                      className="p-2 absolute left-0 xs:hidden"
                    >
                      <ChevronLeft />
                    </button>
                    <div className="flex-1 flex justify-center xs:justify-end">
                      <img
                        src="/images/mls-training-toca-partnership-logo.png"
                        alt="MLS + TOCA"
                        className="h-[64px] max-h-[64px] md:h-[96px] md:max-h-[96px]"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <NewPageHeading
                  text={publicView ? 'Leaderboards' : 'Community Leaderboard'}
                  backButton={showBackButton}
                  onClickOverride={() =>
                    navigate(publicView ? '/' : '/leaderboards', {
                      state: {from: `/leaderboards/${leaderboard?.leaderboard?._id}`},
                    })
                  }
                />
              )}
            </div>
            <div className={styles.container}>
              <div className={styles.infoFlex}>
                <div className={styles.leaderboardInfo}>
                  <LeaderboardInfoPane leaderboard={leaderboard} />
                </div>
                <div>
                  <div className={styles.subHeading}>SEARCH</div>
                  <LeaderboardSearchBar
                    onChange={onSearchChange}
                    indexResults={indexResults}
                    index={searchIndex}
                    setIndex={setSearchIndex}
                  />
                </div>
              </div>
              {items && (
                <div className={styles.listWrapper}>
                  <ScrollableList
                    items={items}
                    itemHeight={itemHeight}
                    heading={<ListHeading />}
                    scrollToItem={scrollToItem}
                    startingItem={startingItem}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </>
    </LeaderboardPageContainer>
  );
};
